'use client'
import {
  AgentCheckUserOutput,
  useAgentCheck,
  useAgentEmailOtp,
  useAgentPhoneOtp,
} from '@repo/services'
import { ButtonLink } from '@repo/shared/components'
import { useErrorHandler, useTranslate } from '@repo/shared/hooks'
import { useRouter } from '@repo/shared/i18n/navigation'
import { PasswordAttemptsManager } from '@repo/shared/utils'
import { Card, Row, Typography, toast } from '@repo/ui/components'
import { normalizeMobile } from '@repo/ui/lib'
import { useState } from 'react'
import { LoginForm } from './login.form'
import { LoginOtp } from './login.otp'
import { LoginPassword } from './login.password'

export const LoginContainer = () => {
  const [loginStep, setLoginStep] = useState<'otp' | 'password' | 'login'>(
    'login',
  )
  const [identifier, setIdentifier] = useState<string>('')
  const [isEmail, setIsEmail] = useState<boolean>(false)
  const [otp, setOtp] = useState<number>(0)
  const { t } = useTranslate()
  const { errorHandler } = useErrorHandler()
  const phoneOtpMutation = useAgentPhoneOtp()
  const emailOtpMutation = useAgentEmailOtp()
  const checkUserMutation = useAgentCheck()
  const [passwordData, setPasswordData] = useState<AgentCheckUserOutput>()
  const { push } = useRouter()

  const handleLoginFormSuccess = (value: string, isEmailValue: boolean) => {
    setIdentifier(value)
    setIsEmail(isEmailValue)

    // Check if user has exceeded password attempts
    const hasExceededAttempts =
      PasswordAttemptsManager.hasReachedMaxAttempts(value)

    checkUserMutation.mutate(
      isEmailValue
        ? { email: value, phone: '' }
        : { phone: normalizeMobile(value), email: '' },
      {
        onSuccess: (res) => {
          if (!res?.exists) {
            toast.error({
              title: t('loginPage_userNotFound'),
            })
            return
          }

          // If user has exceeded password attempts, force OTP login
          if (res?.hasPassword && !hasExceededAttempts) {
            setLoginStep('password')
            setPasswordData(res)
          } else {
            // Use OTP login (either no password or exceeded attempts)
            if (isEmailValue) {
              emailOtpMutation.mutate(
                { email: value },
                {
                  onSuccess: (res) => {
                    setOtp(res?.data?.otp ?? 0)
                    setLoginStep('otp')
                  },
                  onError: errorHandler,
                },
              )
            } else {
              phoneOtpMutation.mutate(
                { phone: normalizeMobile(value) },
                {
                  onSuccess: (res) => {
                    setOtp(res?.data?.otp ?? 0)
                    setLoginStep('otp')
                  },
                  onError: errorHandler,
                },
              )
            }
          }
        },
        onError: errorHandler,
      },
    )
  }

  return (
    <div className='px-md'>
      {loginStep === 'otp' && (
        <LoginOtp
          identifier={identifier}
          otp={otp}
          isEmail={isEmail}
          handleBack={() => setLoginStep('login')}
          redirectingUrl={'/auth/quick-kyc'}
        />
      )}
      {loginStep === 'password' && (
        <LoginPassword
          identifier={identifier}
          isEmail={isEmail}
          handleBack={() => setLoginStep('login')}
          passwordData={passwordData ?? {}}
          onRedirectToOtp={() => {
            // Clear password attempts and redirect to OTP
            PasswordAttemptsManager.clearAttempts()

            push('/auth/forgot-password')
          }}
        />
      )}
      {loginStep === 'login' && (
        <Card className='p-xl rounded-sm gap-y-3xl'>
          <Row className='justify-between'>
            <Typography size='2xl' weight='bold'>
              {t('agents_login_title')}
            </Typography>
            <Row className='gap-xs w-fit'>
              <Typography
                size='2xs'
                weight='regular'
                className='text-neutral-surface-80'
              >
                {t('loginPage_notRegistered')}
              </Typography>
              <ButtonLink size='sm' href={'/auth/register'}>
                {t('common_register')}
              </ButtonLink>
            </Row>
          </Row>
          <Row>
            <LoginForm
              onSuccess={handleLoginFormSuccess}
              loading={
                phoneOtpMutation.isPending ||
                emailOtpMutation.isPending ||
                checkUserMutation.isPending
              }
            />
          </Row>
        </Card>
      )}
    </div>
  )
}
